import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X } from "lucide-react";

const DATA = {
  imovel: {
    rows: [
      ["Juros", "Não há juros: você paga taxa de administração e fundo de reserva", "Incide juros sobre o saldo, além de seguros e encargos"],
      ["Entrada", "Não exige entrada: o plano é feito sob medida", "Normalmente exige uma entrada significativa"],
      ["Aprovação", "Sem análise de renda para entrar no plano", "Depende de análise de crédito e renda"],
      ["FGTS", "Pode ser usado como lance, conforme regras vigentes", "Uso permitido apenas em condições específicas"],
      ["Previsibilidade", "Parcela definida no plano, para você se organizar", "A parcela pode mudar conforme as taxas de mercado"],
    ],
  },
  veiculo: {
    rows: [
      ["Juros", "Não há juros: você paga taxa de administração e fundo de reserva", "Incide juros, muitas vezes altos no financiamento de veículos"],
      ["Entrada", "Sem entrada obrigatória: o plano é planejado", "Costuma exigir entrada para aprovar o crédito"],
      ["Aprovação", "Sem análise de renda para entrar no plano", "Depende de análise de crédito e renda"],
      ["Poder de compra", "Na contemplação, compra à vista — força para negociar desconto", "Compra financiada, sem força de negociação à vista"],
      ["Previsibilidade", "Parcela definida no plano", "A parcela pode mudar conforme as taxas de mercado"],
    ],
  },
};

function CellConsorcio({ children }) {
  return (
    <div className="flex items-start gap-2.5 text-base font-semibold text-slate-800">
      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-600/10 text-brand-600">
        <Check className="h-3.5 w-3.5" />
      </span>
      <span>{children}</span>
    </div>
  );
}

function CellFinanciamento({ children }) {
  return (
    <div className="flex items-start gap-2.5 text-base text-slate-500">
      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <X className="h-3.5 w-3.5" />
      </span>
      <span>{children}</span>
    </div>
  );
}

export default function Comparison() {
  const [tab, setTab] = useState("imovel");

  return (
    <section id="comparativo" className="scroll-mt-24 bg-slate-50 py-24 md:py-32" data-testid="comparison-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">Comparativo honesto</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Consórcio x financiamento, sem enrolação
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Cada modalidade tem o seu contexto. A diferença central: no consórcio você não paga juros —
            paga taxa de administração e fundo de reserva, informados na simulação oficial.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-10"
        >
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid h-auto w-full grid-cols-2 rounded-2xl bg-white p-1.5 shadow-sm border border-slate-200 sm:inline-flex sm:w-auto sm:rounded-full" data-testid="comparison-tabs">
              <TabsTrigger
                value="imovel"
                data-testid="comparison-tab-imovel"
                className="rounded-full px-2 py-2.5 text-xs font-bold data-[state=active]:bg-brand-600 data-[state=active]:text-white sm:px-6 sm:py-2 sm:text-sm"
              >
                Consórcio de imóvel
              </TabsTrigger>
              <TabsTrigger
                value="veiculo"
                data-testid="comparison-tab-veiculo"
                className="rounded-full px-2 py-2.5 text-xs font-bold data-[state=active]:bg-brand-600 data-[state=active]:text-white sm:px-6 sm:py-2 sm:text-sm"
              >
                Consórcio de veículo
              </TabsTrigger>
            </TabsList>

            {["imovel", "veiculo"].map((key) => (
              <TabsContent key={key} value={key} className="mt-6">
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" data-testid={`comparison-table-${key}`}>
                  <div className="hidden grid-cols-[1fr_1.4fr_1.4fr] border-b border-slate-200 bg-brand-600 text-white md:grid">
                    <div className="px-6 py-4 text-xs font-bold uppercase tracking-[0.16em]">Critério</div>
                    <div className="px-6 py-4 text-xs font-bold uppercase tracking-[0.16em]">Consórcio</div>
                    <div className="px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-brand-200">Financiamento</div>
                  </div>
                  {DATA[key].rows.map(([criterio, consorcio, fin], i) => (
                    <div
                      key={criterio}
                      className={`grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-[1fr_1.4fr_1.4fr] md:gap-6 ${i % 2 === 1 ? "bg-slate-50/60" : ""} ${i > 0 ? "border-t border-slate-200" : ""}`}
                    >
                      <div className="font-display text-base font-bold text-slate-900">{criterio}</div>
                      <CellConsorcio>{consorcio}</CellConsorcio>
                      <CellFinanciamento>{fin}</CellFinanciamento>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs leading-relaxed text-slate-400">
                  Comparativo educativo entre modalidades. Condições exatas, taxas e regras do grupo vigente:
                  a Isabela fará a simulação oficial com você.
                </p>
              </TabsContent>
            ))}
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
}
